import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useLocaleTag } from '@/i18n/useLanguage'
import {
  formatDate,
  formatDateLong,
  formatDateShort,
  formatRelativeTime,
  formatTime,
  formatTimestamp,
  type RelativeTimeLabels,
} from '@/lib/format'

/* Locale-bound formatters.
 *
 * DATES are localised: `4 Aug 2026` in English, «4 авг. 2026 г.» in Russian.
 * Month names and ordering are exactly the sort of thing a reader parses
 * pre-attentively, and getting them wrong is what makes a translated product
 * feel translated.
 *
 * NUMBERS AND MONEY ARE NOT, deliberately, and `formatNumber`/`formatMoney` are
 * intentionally absent from this module. Formatting is locale-INDEPENDENT
 * throughout the product — narrow no-break space for thousands, dot for
 * decimals, trailing currency code: `10 080.00 USD` (lib/format § header,
 * DESIGN.md § Typography). Russian convention would write `10 080,00`, and a
 * comma decimal in an invoicing product read by both languages is a live source
 * of error. It also matches how ASA already writes figures in the Master File
 * and in their debit notes. Import those two directly from lib/format.
 */

export function useFormat() {
  const { t } = useTranslation()
  const locale = useLocaleTag()

  return useMemo(() => {
    const relativeLabels: RelativeTimeLabels = {
      now: t('common.relativeTime.now'),
      minutes: (count) => t('common.relativeTime.minutes', { count }),
      hours: (count) => t('common.relativeTime.hours', { count }),
      days: (count) => t('common.relativeTime.days', { count }),
    }

    return {
      locale,
      /** `4 Aug 2026` — a date-only API value, rendered without a zone shift. */
      date: (value: string | null | undefined) => formatDate(value, locale),
      /** `4 Aug` — for a row of dates inside one shipment (lib/format has the
       *  caveat on when the year may be dropped). */
      dateShort: (value: string | null | undefined) => formatDateShort(value, locale),
      /** `Tuesday, 4 August 2026` — the dashboard eyebrow. */
      dateLong: (value: string | null | undefined) => formatDateLong(value, locale),
      /** An event instant in the viewer's zone. */
      timestamp: (value: string | null | undefined) => formatTimestamp(value, locale),
      /** `14:32`, always 24-hour. */
      time: (value: string | null | undefined) => formatTime(value, locale),
      /** `now`, `12 min`, `4 h`, `3 d`, then an absolute date past a week. */
      relative: (value: string | null | undefined, now?: Date) =>
        formatRelativeTime(value, now ?? new Date(), relativeLabels, locale),
    }
  }, [locale, t])
}
